import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { Save as SaveIcon, Send as SendIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../../contexts/AuthContext';

interface EmailConfig {
  id: string;
  host: string;
  port: number;
  username: string;
  password?: string;
  fromEmail: string;
  fromName: string;
  encryption: 'SSL' | 'TLS' | 'NONE';
  isActive: boolean;
  schoolId?: string | null;
}

const EmailConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role?.code === 'SUPER_ADMIN';
  const [config, setConfig] = useState<EmailConfig | null>(null);
  const [isInherited, setIsInherited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    encryption: 'TLS' as 'SSL' | 'TLS' | 'NONE',
    isActive: true,
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Test Dialog State
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testTarget, setTestTarget] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/system-settings/communication/email-config');
      if (data && data.length > 0) {
        const active = data.find((c: EmailConfig) => c.isActive) || data[0];
        const inherited = !isSuperAdmin && !active.schoolId;
        setIsInherited(inherited);
        setConfig(inherited ? null : active);
        setFormData({
          host: active.host,
          port: active.port,
          username: active.username,
          password: '', // Don't show password
          fromEmail: active.fromEmail,
          fromName: active.fromName,
          encryption: active.encryption || 'TLS',
          isActive: active.isActive,
        });
      }
    } catch (error) {
      console.error('Failed to fetch Email config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name as string]: value }));
  };

  const handleSwitchChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.checked }));
  };

  const handleTestOpen = () => {
    setTestTarget('');
    setTestDialogOpen(true);
  };

  const handleTestClose = () => {
    setTestDialogOpen(false);
  };

  const handleTestSubmit = async () => {
    if (!config || !testTarget) return;
    
    setTesting(true);
    try {
      const { data } = await axios.post(`/system-settings/communication/email-config/${config.id}/test`, {
        target: testTarget
      });
      
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        handleTestClose();
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to send test email' 
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      // Ensure port is number
      const payload = {
        ...formData,
        port: Number(formData.port),
      };

      if (config && !isInherited) {
        await axios.patch(`/system-settings/communication/email-config/${config.id}`, payload);
        setMessage({ type: 'success', text: 'Email Server Configuration updated successfully' });
      } else {
        await axios.post('/system-settings/communication/email-config', payload);
        setMessage({ type: 'success', text: isInherited ? 'School email override created successfully' : 'Email Server Configuration created successfully' });
        fetchConfig();
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to save configuration' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <>
      <div className="page-header">
        <h1>Email Server Configuration</h1>
        <p>Configure SMTP or API provider settings for email delivery</p>
      </div>

      <Paper sx={{ p: 3 }}>
        {message && (
          <Alert severity={message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        {isInherited && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You are viewing SuperAdmin default settings. Saving this form will create your school-specific override.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="SMTP Host"
              name="host"
              value={formData.host}
              onChange={handleChange}
              required
              helperText="e.g., smtp.gmail.com"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Port"
              name="port"
              type="number"
              value={formData.port}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth required>
              <InputLabel>Encryption</InputLabel>
              <Select
                name="encryption"
                value={formData.encryption}
                label="Encryption"
                onChange={handleChange as any}
              >
                <MenuItem value="SSL">SSL</MenuItem>
                <MenuItem value="TLS">TLS</MenuItem>
                <MenuItem value="NONE">NONE</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Username / API User"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Password / API Key"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={config ? 'Leave blank to keep unchanged' : ''}
              required={!config}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="From Email"
              name="fromEmail"
              type="email"
              value={formData.fromEmail}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="From Name"
              name="fromName"
              value={formData.fromName}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleSwitchChange('isActive')}
                    color="primary"
                  />
                }
                label={formData.isActive ? "Server Active" : "Server Inactive"}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
            
            {config && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<SendIcon />}
                onClick={handleTestOpen}
                disabled={!config.isActive}
              >
                Test Configuration
              </Button>
            )}
          </Grid>
        </Grid>
      </form>

      <Dialog open={testDialogOpen} onClose={handleTestClose}>
        <DialogTitle>Test Email Configuration</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter an email address to send a test email to. This will verify your SMTP settings.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Target Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={testTarget}
            onChange={(e) => setTestTarget(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTestClose}>Cancel</Button>
          <Button 
            onClick={handleTestSubmit} 
            variant="contained" 
            disabled={!testTarget || testing}
          >
            {testing ? 'Sending...' : 'Send Test Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
    </>
  );
};

export default EmailConfigPage;
